import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '918618244782';
const PHONE_NUMBER = '+918880331224';
const EMAIL = 'info@akrutiengineersandbuilders.com';
const ADDRESS = 'T NO.2, AT BODHAN TQ ALAND, KALABURAGI, Kalaburagi (Gulbarga), Karnataka, 585101';

const projectTypes = [
  'Residential Construction',
  'Commercial Construction',
  'Interior Design',
  'Renovation',
  'Turnkey Project',
  'Architectural Planning',
  'Other'
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Send to WhatsApp
    const message = encodeURIComponent(
      `*New Enquiry from Website*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Phone:* ${formData.phone}\n` +
      `*Email:* ${formData.email}\n` +
      `*Project Type:* ${formData.projectType}\n` +
      `*Message:* ${formData.message}`
    );
    
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      projectType: '',
      message: ''
    });
    setIsSubmitting(false);
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('Hello! I would like to enquire about your construction and interior services.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="noise-overlay pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ececec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              Get In Touch
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mt-4">
              Let's Build Your<br />
              <span className="text-gold-gradient">Dream Together</span>
            </h1>
            <p className="text-gray-600 text-lg mt-6 leading-relaxed">
              Have a project in mind? Get in touch with our team for a free consultation 
              and quote. We're here to help you every step of the way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 md:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-gray-900">
                Contact Information
              </h2>
              <p className="text-gray-600 mt-4">
                Reach out to us through any of the following channels. 
                We typically respond within 24 hours.
              </p>

              <div className="mt-10 space-y-8">
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white group-hover:bg-[#C9A646] transition-colors rounded-xl shadow-lg shadow-gray-300/50">
                    <Phone size={22} className="text-[#C9A646] group-hover:text-[#0A0A0A]" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">Phone</h4>
                    <p className="text-gray-600 mt-1">+91 88803 31224</p>
                  </div>
                </a>

                <button 
                  onClick={handleWhatsApp}
                  className="flex items-start gap-4 group w-full text-left"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white group-hover:bg-[#25D366] transition-colors rounded-xl shadow-lg shadow-gray-300/50">
                    <MessageCircle size={22} className="text-[#25D366] group-hover:text-gray-900" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">WhatsApp</h4>
                    <p className="text-gray-600 mt-1">+91 86182 44782</p>
                  </div>
                </button>

                <a 
                  href={`mailto:${EMAIL}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white group-hover:bg-[#C9A646] transition-colors rounded-xl shadow-lg shadow-gray-300/50">
                    <Mail size={22} className="text-[#C9A646] group-hover:text-[#0A0A0A]" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">Email</h4>
                    <p className="text-gray-600 mt-1 break-all">{EMAIL}</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl shadow-lg shadow-gray-300/50">
                    <MapPin size={22} className="text-[#C9A646]" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">Office Address</h4>
                    <p className="text-gray-600 mt-1">{ADDRESS}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl shadow-lg shadow-gray-300/50">
                    <Clock size={22} className="text-[#C9A646]" />
                  </div>
                  <div>
                    <h4 className="text-gray-900 font-medium">Working Hours</h4>
                    <p className="text-gray-600 mt-1">Mon - Sat: 9:00 AM - 7:00 PM</p>
                    <p className="text-gray-600">Sunday: By Appointment</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="glass-card p-8 md:p-10">
                <h2 className="font-heading text-3xl text-gray-900 mb-8">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name *"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      data-testid="contact-phone-input"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="form-input bg-transparent"
                      data-testid="contact-project-select"
                    >
                      <option value="" disabled className="bg-white">Select Project Type *</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type} className="bg-white">
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      required
                      className="form-input resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Message via WhatsApp
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24 bg-[#ececec] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-4xl md:text-5xl text-gray-900">
              Find Us Here
            </h2>
            <p className="text-gray-600 mt-4">
              Visit our office for a personal consultation
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative overflow-hidden"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121819.39282977957!2d76.75!3d17.33!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc8c7be35cd8e85%3A0x12ce27e3d01a3daa!2sKalaburagi%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0, filter: 'grayscale(100%) contrast(1.1)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
              className="opacity-70 hover:opacity-100 transition-opacity duration-500"
            />
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 bg-[#f5f5f5] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href={`tel:${PHONE_NUMBER}`}
              className="flex items-center justify-center gap-3 p-6 bg-gradient-to-r from-[#C9A646] to-[#E6C36A] text-[#0A0A0A] font-semibold hover:opacity-90 transition-opacity"
              data-testid="quick-call-btn"
            >
              <Phone size={24} />
              Call Now: +91 88803 31224
            </a>
            <button
              onClick={handleWhatsApp}
              className="flex items-center justify-center gap-3 p-6 bg-[#25D366] text-gray-900 font-semibold hover:opacity-90 transition-opacity"
              data-testid="quick-whatsapp-btn"
            >
              <MessageCircle size={24} />
              Chat on WhatsApp
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
